import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CONTENT } from "@/content";

const LINKS = [
  { label: "Approche", href: "#approche" },
  { label: "À propos", href: "#a-propos" },
  { label: "Le cabinet", href: "#cabinet" },
  { label: "Motifs", href: "#motifs" },
  { label: "Conseils", href: "#conseils" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact", href: "#contact" },
];

export const scrollToSection = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72, duration: 1.6 });
  else document.querySelector(href)?.scrollIntoView();
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToSection(href), 150);
  };

  return (
    <>
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-ivory/90 backdrop-blur-md border-b border-beige/70" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => scrollToSection("#hero")}
          className="flex items-center gap-3 text-left leading-none"
        >
          <span className="logo-shine-wrap" data-testid="nav-logo-emblem">
            <img src="/logo-emblem.png" alt={`Logo ${CONTENT.name}`} className="logo-glow h-11 w-auto" />
            <span
              className="logo-shine"
              aria-hidden="true"
              style={{
                WebkitMaskImage: "url('/logo-emblem.png')",
                maskImage: "url('/logo-emblem.png')",
                WebkitMaskSize: "contain",
                maskSize: "contain",
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                maskPosition: "center",
              }}
            />
          </span>
          <span>
            <span className="block font-serif text-xl tracking-wide text-anthracite">{CONTENT.name}</span>
            <span className="mt-1 block text-[10px] uppercase tracking-[0.3em] text-taupe/80">
              {CONTENT.title}
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-9 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => scrollToSection(l.href)}
              className="text-xs tracking-[0.18em] uppercase text-taupe transition-colors duration-300 hover:text-anthracite"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-booking-button"
            href={CONTENT.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-shine rounded-md bg-sage px-6 py-3 text-xs font-medium uppercase tracking-[0.14em] text-ivory transition-colors duration-300 hover:bg-sage-dark"
          >
            Rendez-vous
          </a>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <a
            data-testid="nav-booking-button-mobile"
            href={CONTENT.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-shine rounded-md bg-sage px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.14em] text-ivory"
          >
            RDV
          </a>
          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Ouvrir le menu"
            className="flex h-10 w-10 items-center justify-center rounded-md border border-sage-light/70 bg-ivory/80 text-anthracite"
          >
            {open ? <X className="h-5 w-5" strokeWidth={1.2} /> : <Menu className="h-5 w-5" strokeWidth={1.2} />}
          </button>
        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="grain fixed inset-0 z-40 flex flex-col justify-center bg-ivory px-8 pt-20 lg:hidden"
          >
            <nav className="space-y-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => go(l.href)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="block w-full border-b border-beige/70 py-4 text-left font-serif text-3xl font-light text-anthracite"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <motion.a
              data-testid="nav-mobile-booking-button"
              href={CONTENT.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="btn-shine mt-10 inline-block w-fit rounded-md bg-sage px-9 py-4 text-sm font-medium uppercase tracking-[0.14em] text-ivory"
            >
              Prendre rendez-vous
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
